// src/pages/Manager/System.jsx
// STATUS: active | UPDATED: 2026-09-24
// LOG:
//   2026-09-24  Moved from index.jsx to /manager/system/. `/manager/` is now
//               the day rather than the reference: a manager opening this
//               between customers wants to know what is not done yet, and had
//               to pick from five pages to find out.
//   2026-09-17  New. The manager layer, finally reading the extract that has
//               been sitting in the repo since August.
//
// Running the floor. Jazz's own management system, from the 2026 Trainer
// Edition, which we parsed in August and then never imported anywhere. The
// Academy has been describing this level as "not started" ever since.
//
// The audit is interactive and everything else is reference, and that split is
// on purpose. The audit is the only part that is a daily habit rather than
// something you read once, and a checklist you cannot tick is a poster.

import { useCallback, useEffect, useState } from 'react'
import { Box, HStack, VStack, Text, Checkbox, Spinner, Alert, AlertIcon, Divider } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { MANAGEMENT_SYSTEM } from '../../content/manager'
import { getToday, saveToday, recentDays, localToday } from '../../lib/managerAudit'
import { useAuth } from '../../hooks/useAuth'

const { dailyDisciplines, managerDailyAudit, weeklyCoachingRhythm, oneOnOneGuide, coreScorecard } =
  MANAGEMENT_SYSTEM

function Plain({ title, note, items }) {
  return (
    <Box>
      <Text fontSize="lg" fontWeight={600} mb={note ? 0.5 : 2}>
        {title}
      </Text>
      {note && (
        <Text fontSize="body" color="inkMuted" mb={2} maxW="measure">
          {note}
        </Text>
      )}
      <VStack align="stretch" spacing={0}>
        {items.map((item) => {
          const name = typeof item === 'string' ? null : item.name
          const text = typeof item === 'string' ? item : item.description
          return (
            <Box key={name || text} py={3} borderTop="1px solid" borderColor="line">
              {name && (
                <Text fontWeight={600} fontSize="body" mb={0.5}>
                  {name}
                </Text>
              )}
              <Text fontSize="body" color={name ? 'inkMuted' : 'ink'} maxW="measure" lineHeight={1.6}>
                {text}
              </Text>
            </Box>
          )
        })}
      </VStack>
    </Box>
  )
}

export default function ManagerSystem() {
  const { user } = useAuth()
  const [state, setState] = useState({ loading: true, checked: [], days: [], error: null })
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    const [{ data: today, error }, { data: days }] = await Promise.all([
      getToday(user?.id),
      recentDays(user?.id),
    ])
    setState({
      loading: false,
      checked: today?.items || [],
      days: days || [],
      error: error ? error.message : null,
    })
  }, [user?.id])

  useEffect(() => {
    load()
  }, [load])

  const toggle = async (index) => {
    const next = state.checked.includes(index)
      ? state.checked.filter((n) => n !== index)
      : [...state.checked, index]
    // Optimistic, because a checkbox that waits on a round trip feels broken.
    setState((s) => ({ ...s, checked: next }))
    setSaving(true)
    const { error } = await saveToday(user?.id, next)
    setSaving(false)
    if (error) {
      setState((s) => ({ ...s, checked: s.checked, error: error.message || String(error) }))
      load()
    }
  }

  const done = state.checked.length
  const total = managerDailyAudit.length

  return (
    <VStack align="stretch" spacing={10} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          For the people who run floors
        </Text>
        <Text textStyle="pageTitle">Running the floor</Text>
        <Text fontSize="lg" color="inkMuted" mt={2} maxW="measure" lineHeight={1.55}>
          Jazz's management system, as he wrote it. The audit at the top is yours to work through at the
          end of a shift. Everything under it is there when you want it.
        </Text>
      </Box>

      {state.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      <Box>
        <HStack justify="space-between" align="baseline" mb={0.5} flexWrap="wrap" gap={2}>
          <Text fontSize="lg" fontWeight={600}>
            Today
          </Text>
          <Text fontSize="sm" color="inkDim" fontFamily="mono">
            {done} of {total}
            {saving ? ' • saving' : ''}
          </Text>
        </HStack>
        <Text fontSize="body" color="inkMuted" mb={3} maxW="measure">
          {localToday()}. Nobody sees this but you and Jazz, and he is looking at whether the inspecting
          happens at all rather than at your score.
        </Text>

        {state.loading ? (
          <HStack spacing={3} color="inkMuted" py={3}>
            <Spinner size="sm" />
            <Text fontSize="body">Loading</Text>
          </HStack>
        ) : (
          <VStack align="stretch" spacing={0}>
            {managerDailyAudit.map((item, i) => (
              <Box key={item} py={2.5} borderTop="1px solid" borderColor="line">
                <Checkbox
                  id={`audit-${i}`}
                  isChecked={state.checked.includes(i)}
                  onChange={() => toggle(i)}
                  alignItems="flex-start"
                  sx={{ '.chakra-checkbox__control': { mt: '3px' } }}
                >
                  <Text
                    fontSize="body"
                    lineHeight={1.5}
                    color={state.checked.includes(i) ? 'inkDim' : 'ink'}
                  >
                    {item}
                  </Text>
                </Checkbox>
              </Box>
            ))}
          </VStack>
        )}
      </Box>

      <Divider borderColor="line" />

      <Plain
        title="Your daily disciplines"
        note="The shape of the day, before the audit checks it happened."
        items={dailyDisciplines}
      />

      <Plain
        title="Every week"
        note="The coaching that does not happen unless it is scheduled."
        items={weeklyCoachingRhythm}
      />

      <Plain
        title="The one on one"
        note="Jazz on how to run it, including the language. Describe the behaviour, explain the impact, demonstrate the correction, require a repetition and set a time to inspect it again."
        items={oneOnOneGuide}
      />

      <Plain
        title="The scorecard"
        note="Six categories. Units and gross sit inside Results, which is deliberately not the first thing on the list."
        items={coreScorecard}
      />

      <HStack
        as={RouterLink}
        to="/manager/steps/"
        justify="space-between"
        align="center"
        py={5}
        borderTop="1px solid"
        borderColor="line"
        _hover={{ textDecoration: 'none', '& .go': { transform: 'translateX(3px)' } }}
      >
        <Box>
          <Text fontWeight={600} color="ink">
            The thirteen steps, from your side
          </Text>
          <Text fontSize="body-sm" color="inkMuted" mt={0.5}>
            What to watch for, how to coach it, the role play lab and what you are signing off against.
          </Text>
        </Box>
        <Box className="go" color="inkDim" transition="transform 0.15s ease">
          <ChevronRight size={18} />
        </Box>
      </HStack>
    </VStack>
  )
}
